/**
 * Lexicanon theme runtime — the single source of truth for applying one of the
 * 8 selectable themes to the document. The theme *keys/labels/default* live in
 * `@lexicanon/shared` (so the backend validates against the same set); this
 * module adds the browser-only concerns: the per-theme Google-Fonts href map
 * and `applyLexTheme()`, which sets the `data-theme` attribute, swaps the font
 * stylesheet, and caches the choice for the next pre-paint.
 *
 * Resolution order (highest wins), implemented across `main.tsx` (pre-paint)
 * and `_auth.tsx` (post-auth):
 *   1. `?theme=` — transient preview (sessionStorage; never cached to localStorage)
 *   2. user override — `user.theme_preference` (post-auth, authoritative)
 *   3. cached `lex:theme` (localStorage) — device continuity before auth resolves
 *   4. company default — `branding.defaultTheme` (available pre-auth on login)
 *   5. `pastel` — global default
 */

import { LEX_DEFAULT_THEME, LEX_THEME_KEYS, LEX_THEME_LABELS, type TLexThemeKey } from "@lexicanon/shared"

export { LEX_DEFAULT_THEME, LEX_THEME_KEYS, LEX_THEME_LABELS, type TLexThemeKey }

/** localStorage key for the persisted per-device theme (shared with main.tsx). */
const LEX_THEME_STORAGE_KEY = "lex:theme"
/** sessionStorage key marking a transient `?theme=` preview for this tab. */
const LEX_THEME_PREVIEW_KEY = "lex:theme-preview"
/** id on the injected font <link> so we can swap its href at runtime. */
const LEX_FONT_LINK_ID = "lex-font-link"

const JETBRAINS = "JetBrains+Mono:wght@400;500"

const DM_SANS = `https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300..700;1,9..40,300..700&family=${JETBRAINS}&display=swap`
const PLEX_FRAUNCES = `https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT@0,9..144,300..800,0..100;1,9..144,300..800,0..100&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap`

/** Per-theme webfont stylesheet. JetBrains Mono is always included. */
const LEX_THEME_FONTS: Record<TLexThemeKey, string> = {
  editorial: PLEX_FRAUNCES,
  bordeaux: PLEX_FRAUNCES,
  warm: `https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&family=${JETBRAINS}&display=swap`,
  signal: DM_SANS,
  classic: DM_SANS,
  evergreen: `https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400..600&family=DM+Sans:ital,opsz,wght@0,9..40,300..700;1,9..40,300..700&family=${JETBRAINS}&display=swap`,
  ink: `https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=${JETBRAINS}&display=swap`,
  pastel: `https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&family=${JETBRAINS}&display=swap`,
}

function isLexTheme(value: unknown): value is TLexThemeKey {
  return typeof value === "string" && (LEX_THEME_KEYS as readonly string[]).includes(value)
}

/** Coerce any value to a valid theme key, falling back to the global default. */
export function coerceLexTheme(value: unknown): TLexThemeKey {
  return isLexTheme(value) ? value : LEX_DEFAULT_THEME
}

/** The theme currently painted on <html> (classic = no data-theme attribute). */
export function readActiveLexTheme(): TLexThemeKey {
  if (typeof document === "undefined") return LEX_DEFAULT_THEME
  const attr = document.documentElement.getAttribute("data-theme")
  return isLexTheme(attr) ? attr : "classic"
}

function readStorage(storage: "local" | "session", key: string): string | null {
  try {
    const store = storage === "local" ? window.localStorage : window.sessionStorage
    return store.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(storage: "local" | "session", key: string, value: string | null): void {
  try {
    const store = storage === "local" ? window.localStorage : window.sessionStorage
    if (value === null) store.removeItem(key)
    else store.setItem(key, value)
  } catch {
    /* ignore — private mode / disabled storage is non-fatal */
  }
}

/** The persisted per-device theme, if any (used for pre-paint continuity). */
function readCachedLexTheme(): TLexThemeKey | null {
  const v = readStorage("local", LEX_THEME_STORAGE_KEY)
  return isLexTheme(v) ? v : null
}

/** A `?theme=` preview active for this tab, if any. */
export function readPreviewLexTheme(): TLexThemeKey | null {
  const v = readStorage("session", LEX_THEME_PREVIEW_KEY)
  return isLexTheme(v) ? v : null
}

function setPreviewLexTheme(theme: TLexThemeKey | null): void {
  writeStorage("session", LEX_THEME_PREVIEW_KEY, theme)
}

/**
 * Apply a theme to the live document: set/remove `data-theme`, swap the font
 * stylesheet, and (unless `cache: false`) persist it for the next pre-paint.
 * `classic` is the attribute-less baseline, so its attribute is removed.
 */
export function applyLexTheme(theme: TLexThemeKey, opts: { cache?: boolean } = {}): void {
  if (typeof document === "undefined") return
  const root = document.documentElement
  if (theme === "classic") root.removeAttribute("data-theme")
  else root.setAttribute("data-theme", theme)

  const href = LEX_THEME_FONTS[theme]
  let link = document.getElementById(LEX_FONT_LINK_ID) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement("link")
    link.id = LEX_FONT_LINK_ID
    link.rel = "stylesheet"
    document.head.appendChild(link)
  }
  if (link.href !== href) link.href = href

  if (opts.cache !== false) writeStorage("local", LEX_THEME_STORAGE_KEY, theme)
}

/**
 * Pre-paint resolution (synchronous, no branding/user yet): a `?theme=` param
 * is a transient preview (recorded in sessionStorage, NOT cached); otherwise
 * use the cached per-device theme, else the global default. Returns the key it
 * applied so the caller can swap fonts in the same tick if it wants.
 */
export function resolveAndApplyInitialLexTheme(search: string): TLexThemeKey {
  let requested = new URLSearchParams(search).get("theme")
  if (requested === "default") requested = "classic" // legacy bookmark alias
  if (isLexTheme(requested)) {
    setPreviewLexTheme(requested)
    applyLexTheme(requested, { cache: false })
    return requested
  }
  // No (valid) preview request — clear any stale preview from a prior nav.
  setPreviewLexTheme(null)
  const theme = readCachedLexTheme() ?? LEX_DEFAULT_THEME
  applyLexTheme(theme, { cache: false })
  return theme
}

/**
 * Apply the company default ONLY when the user hasn't expressed a choice — i.e.
 * no active `?theme=` preview and no cached per-device theme. Called once the
 * tenant branding resolves (themes the login page pre-auth). Does not cache, so
 * it never shadows a future personal choice.
 */
export function applyCompanyDefaultLexTheme(companyDefault: TLexThemeKey | null): void {
  if (!companyDefault) return
  if (readPreviewLexTheme()) return
  if (readCachedLexTheme()) return
  applyLexTheme(companyDefault, { cache: false })
}
